import { useEffect, useMemo, useRef, useState } from "react"
import { useDispatch, useSelector, shallowEqual } from "react-redux"
import Button from "react-bootstrap/Button"
import Table from "react-bootstrap/Table"
import "./index.scss"

import { FiAlertTriangle, FiPlus, FiTrash2 } from "react-icons/fi"

import { Title, Text, Checkbox, Container } from "components/Form"
import { Popup } from "components"
import ESignatureModal from "components/Lims/ESignatureModal"
import StatusBadge, { StatusBadgeStack } from "components/Lims/StatusBadge"
import QaDecisionSection from "./QaDecisionSection"
import PhaseSection from "./PhaseSection"
import AuditSection from "./AuditSection"
import OosInvestigationModal from "components/Lims/OosInvestigationModal"
import WorklistModal from "components/Lims/WorklistModal"
import { limsResults, limsSpecs, limsConsumables, message, limsReviews, limsReviewSpec, samples, limsSstSpec, limsSstResult, limsReruns, limsOos, limsWorklistTemplates, limsSampleWorklists } from "state_management"
import { InvalidParametersError } from "utils/request"
import { canSign, isGmpRelaxed } from "state_management/lims/segregationOfDuties"
import { hasQcCapability } from "state_management/lims/limsCapabilities"
import {
  consumableType,
  isStandardBlocking,
  isStandardExpiringSoon,
  standardStatus,
  validityStatus,
} from "state_management/lims/limsConsumables"
import useT from "utils/hooks/useT"
import { displaySampleId } from "utils/scripts/sampleId"

// ResultEntryPanel (F-C2 surface a; QC lifecycle reworked in SPEC-011) — PER-SAMPLE QC results for
// one analysis. The same analysis runs on all its samples (which may be from different products)
// against the same F-B1 spec, and each sample is judged individually. The analyst enters a value
// per parameter (auto-judged against the criterion) — inputs are ALWAYS enabled while the result
// is a draft. The computed Judgement (Pass/Fail vs the spec) is the source of truth and is shown
// for every sample: there is no analyst "OK" that can overrule it.
//
// The ONE exception (R19/R22, #4084): a method with NO specification parameters has nothing to
// judge, so there is no computed judgement to be the source of truth. Such a sample carries an
// analyst OK instead — it is the only content the sample can have, and without it "Send to review"
// could never activate (`sampleHasContent` looked only at entered parameter values). The OK is not
// an override and cannot exist alongside parameters; the server judges the saved result PASS
// (SPEC-056 R20) and the existing Pass badge shows it.
//
// Lifecycle (SPEC-011): the analyst clicks "Send to review" (NO e-signature) → the results go
// `in-review` and a "Review <analysis>" action appears in the right-panel Reviews section. A
// qualified reviewer (NOT an enterer, holding the "reviewer" capability — enforced by the
// e-signature dialog) opens the analysis and, per sample, may:
//   - mark OOT (out-of-trend) + comment — informational, not e-signed; or
//   - set okOverride — forces a computed Fail to an effective Pass; e-SIGNED. The raw computed
//     Fail stays visible ("Fail (override)").
// The reviewer then either "Send to QA" (the reviewer's e-SIGNED sign-off → `sent-to-qa`; QA's
// per-sample Approve/RERUN/STOP arrives in SPEC-012) or "Return for correction" (→ `draft` with a
// mandatory comment, and a "Correct <analysis>" task is created for the analyst). A computed Fail
// is an OOS at entry by definition; we NO LONGER flag the sample or raise a separate OOS record.
//
// On send: the applied spec is snapshotted onto each sample's record (GMP). The spec-level
// free-text formula is shown as read-only reference.
//
// SPEC-016 (PRD-005 Area 2) — CALCULATED parameters: a parameter may be MEASURED (a single value
// cell, as above) or CALCULATED. For a calculated parameter the analyst enters the parameter's
// named raw inputs (one field per declared input; an `aggregate` input gets a small add/remove
// list editor for the n values), the calculation engine derives + judges the value LIVE, and the
// rounded computed value + judgement are shown in place of a value cell. On save the entered
// inputs, the formula used and the full-precision computed value are snapshotted onto the value
// (GMP reproducibility) via buildCalculatedValue. An input whose `source` is "standardPurity" /
// "standardWaterContent" is auto-filled from the selected reference-standard consumable's
// `purityPercent` / `waterContentPercent` field IF that field is present on the record (a
// defensive read — the analyst may always override; no reference-standards-specific selector is
// imported). A calc error is shown inline and the judgement falls back to "—" (no crash).
//
// The instrument and the consumable lots are chosen on the analysis task itself (the instrument
// is the existing Primary Instrument; the consumables sit under Additional Information), NOT
// here — both are passed in and snapshotted onto each record. Instrument readiness is enforced
// by Plannertech's existing maintenance scheduling (an overdue instrument is blocked by a task
// and cannot be booked), so this panel adds no instrument warning of its own.
//
// Props: analysis (id, name), analysisDefinitionId (may be null → "no specification"),
// primaryAssetId (the analysis's Primary Instrument id, or null), reagentLotIds (the run's
// chosen consumable ids, snapshotted onto each record — the field name is kept for back-compat).
export default function ResultEntryPanel({
  analysis,
  analysisDefinitionId,
  primaryAssetId = null,
  reagentLotIds = [],
}) {
  const { t } = useT("common")
  const dispatch = useDispatch()
  const analysisId = analysis ? analysis.id : null

  const spec = useSelector((state) =>
    analysisDefinitionId ? limsSpecs.selectSpec(state, analysisDefinitionId) : undefined
  )
  // shallowEqual: selectResultsForAnalysis / the `|| []` fallback return a fresh array each call,
  // which would otherwise make these selectors change identity on EVERY render and re-run the
  // seeding effect below — wiping whatever the analyst is typing. shallowEqual keeps the previous
  // reference whenever the contents are unchanged, so local edits survive re-renders.
  const sampleItems = useSelector(
    (state) => (state.samples[analysisId] && state.samples[analysisId].items) || [],
    shallowEqual
  )
  const existingResults = useSelector(
    (state) => (analysisId ? limsResults.selectResultsForAnalysis(state, analysisId) : []),
    shallowEqual
  )
  // The current user (the enterer doing the send) — excluded from signing the standard-gate
  // override (4-eyes): a deviation on standard validity must be signed by a DIFFERENT qualified
  // person (SPEC-015).
  const currentMemberId = useSelector((state) => state.auth.memberId)
  // Full consumable records for the run's chosen consumables. Two consumers: the SPEC-015 reference-
  // standard hard gate (evaluated LOCALLY, no network dependency), and the SPEC-016 DEFENSIVE
  // auto-fill of a calculated input whose source is a reference-standard field (read
  // `purityPercent` / `waterContentPercent` off a selected consumable IF present — never assuming a
  // reference-standards schema; the field may simply be absent → the input stays manual).
  // selectConsumables returns the loaded register; shallowEqual keeps a stable identity.
  const allConsumables = useSelector(limsConsumables.selectConsumables, shallowEqual)

  // SPEC-017 System Suitability hard gate: when this analysis definition REQUIRES an SST, results
  // cannot be sent to review until the SST has PASSED and been SIGNED. Both are read locally — no
  // network dependency at send time — but SPEC-058 changed what supplies the second answer: the
  // SERVER computes the verdict (against the criteria the run was actually judged by) and publishes
  // it on the SST record, and isSstPassedAndSigned reads it instead of re-judging here. Fail-closed
  // if it is missing. The old local re-judgement drifted from the server's and produced #4130.
  const requiresSst = useSelector((state) =>
    analysisDefinitionId ? limsSstSpec.selectRequiresSst(state, analysisDefinitionId) : false
  )
  const sstReady = useSelector((state) =>
    requiresSst && analysisId ? limsSstResult.isSstPassedAndSigned(state, analysisId) : false
  )
  // The saved SST record for this run (or undefined) — used to stamp the deviation signature when
  // the SST gate is overridden.
  const sstRecord = useSelector(
    (state) => (requiresSst && analysisId ? limsSstResult.selectSstResult(state, analysisId) : undefined),
    shallowEqual
  )

  // SPEC-035 (F3): whether this analysis requires the supervisor (chemist) review step (the F0
  // requiresSupervisorReview flag on the Review Specification). When set, the reviewer signs results
  // on to the supervisor (not straight to QA), and the supervisor phase renders between reviewer
  // and QA. Default false → the unchanged analyst → reviewer → QA flow.
  const requiresSupervisorReview = useSelector((state) => {
    const spec = analysisDefinitionId ? limsReviewSpec.selectReviewSpec(state, analysisDefinitionId) : undefined
    return !!(spec && spec.requiresSupervisorReview)
  })

  // Rerun links (SPEC-022 F2 / K16) — the full set, so each sample card can show its rerun
  // traceability BOTH ways: an original shows "Rerun created → <number>"; a rerun shows "Rerun of
  // <number>". shallowEqual keeps a stable reference between unrelated re-renders.
  const rerunLinks = useSelector(limsReruns.selectReruns, shallowEqual)
  // RR2 (#10/Q2): all OOS records, so a FAIL row can reopen an existing investigation or open a new one.
  const allOos = useSelector(limsOos.selectAllOos, shallowEqual)

  // F9 (SPEC-041): whether this analysis definition has an EFFECTIVE sample-prep worklist template
  // OF ITS OWN. This is the GATE: a SIGNED per-sample worklist is the precondition for the reviewer
  // hand-off (send to supervisor / QA) — fail-fast here, authoritative server-side. SPECIFIC-ONLY so
  // a generic template never blanket-gates every analysis.
  const hasWorklistTemplate = useSelector((state) =>
    analysisDefinitionId ? limsWorklistTemplates.selectHasEffectiveTemplate(state, analysisDefinitionId) : false
  )
  // RR7 (#846): whether a worklist can be FILLED for this analysis — its own specific template, ELSE
  // the generic effective example (specific ?? generic). This drives the per-sample "Åbn worklist"
  // LINK so the analyst still gets a usable checklist when the analysis has no template of its own.
  // Separate from hasWorklistTemplate (the gate stays specific-only); without this the generic
  // fallback in selectEffectiveForDefinition / WorklistModal is unreachable from the UI.
  const hasWorklistForFill = useSelector((state) =>
    analysisDefinitionId ? limsWorklistTemplates.selectEffectiveForDefinition(state, analysisDefinitionId) !== null : false
  )
  const sampleWorklists = useSelector(limsSampleWorklists.selectAllWorklists, shallowEqual)

  const parameters = useMemo(() => (spec && spec.parameters) || [], [spec])
  const formula = (spec && spec.formula) || ""
  // F8 (SPEC-040): the analysis's consumable requirements (off the folded spec). The send-to-review
  // completeness gate requires every requirement to be covered by ≥1 selected consumable (the FE
  // is fail-fast; the server is authoritative with a 422).
  const requiredConsumables = useMemo(() => (spec && spec.requiredConsumables) || [], [spec])

  // Has at least one calculated parameter? Drives whether we load consumables for auto-fill.
  const hasCalculated = useMemo(() => parameters.some(limsSpecs.isCalculatedParameter), [parameters])

  // Defensively resolve the auto-fill value for a calculated input's `source` (SPEC-016). Scans the
  // selected run consumables for the FIRST that carries the relevant numeric field (purityPercent for
  // standardPurity, waterContentPercent for standardWaterContent). Returns a number, or null when
  // the source is "manual" / no selected consumable carries the field (→ the analyst enters it).
  const autoFillForSource = useMemo(() => {
    const selected = allConsumables.filter((c) => reagentLotIds.includes(c.id))
    const fieldFor = { standardPurity: "purityPercent", standardWaterContent: "waterContentPercent" }
    return (source) => {
      const field = fieldFor[source]
      if (!field) return null
      for (const c of selected) {
        const v = c && c[field]
        if (v !== null && v !== undefined && v !== "" && Number.isFinite(Number(v))) return Number(v)
      }
      return null
    }
  }, [allConsumables, reagentLotIds])

  // The selected reference standards split into blocking (expired/discarded → HARD gate) and
  // soft-warning (quarantine / expiring-soon → warn, never block) sets (SPEC-015). Computed from
  // the local register; non-standard consumables are ignored here.
  const selectedStandards = useMemo(
    () => reagentLotIds.map((id) => allConsumables.find((c) => c.id === id)).filter((c) => c && consumableType(c) === "standard"),
    [reagentLotIds, allConsumables]
  )
  const blockingStandards = useMemo(() => selectedStandards.filter((c) => isStandardBlocking(c)), [selectedStandards])
  const warningStandards = useMemo(
    () => selectedStandards.filter((c) => !isStandardBlocking(c) && (standardStatus(c) === "quarantine" || isStandardExpiringSoon(c))),
    [selectedStandards]
  )

  // F8 (SPEC-040): the requirements NOT covered by any selected consumable — the fail-fast
  // completeness gate (mirrors the BE `_uncovered_requirements`). A consumable covers a requirement
  // when it is active, its type matches, and (for a standard) its standardClass matches. Quantity is
  // advisory and not gated.
  const selectedConsumableRecords = useMemo(
    () => reagentLotIds.map((id) => allConsumables.find((c) => c.id === id)).filter(Boolean),
    [reagentLotIds, allConsumables]
  )
  const uncoveredRequirements = useMemo(
    () => requiredConsumables.filter((req) => !selectedConsumableRecords.some((c) => consumableCoversRequirement(c, req))),
    [requiredConsumables, selectedConsumableRecords]
  )

  // Load the analysis's samples.
  useEffect(() => {
    if (analysisId) dispatch(samples.fetchForPlanned(analysisId))
  }, [dispatch, analysisId])

  // Load the rerun links so a sample card can show its rerun traceability both ways (K16). The
  // panel only ever renders inside the LIMS QC section, so this is gated by analysisId alone.
  useEffect(() => {
    if (analysisId) dispatch(limsReruns.fetch())
  }, [dispatch, analysisId])

  // RR2 (#10/Q2): load OOS records so the FAIL-row "Open OOS investigation" action knows whether an
  // investigation is already open for the sample (reopen) vs. needs creating.
  useEffect(() => {
    if (analysisId) dispatch(limsOos.fetch())
  }, [dispatch, analysisId])

  // F9 (SPEC-041): load the worklist templates (to know whether this analysis has an effective one)
  // and the team's sample worklists (so the send gate + the per-sample link state read signed-ness
  // without a round-trip per sample). Inert when showLims is off (this panel does not mount).
  useEffect(() => {
    if (analysisId) {
      dispatch(limsWorklistTemplates.fetch())
      dispatch(limsSampleWorklists.fetch())
    }
  }, [dispatch, analysisId])

  // Load the consumable register when a calculated parameter may auto-fill from a reference
  // standard OR when the analysis declares consumable requirements (F8 — the completeness gate
  // needs the live consumable records). Measured-only specs with no requirements never touch it
  // (and `showLims` off renders nothing here at all).
  useEffect(() => {
    if (hasCalculated || requiredConsumables.length > 0) dispatch(limsConsumables.fetch())
  }, [dispatch, hasCalculated, requiredConsumables.length])

  const resultBySample = useMemo(() => {
    const map = {}
    existingResults.forEach((r) => {
      if (r.sampleId) map[r.sampleId] = r
    })
    return map
  }, [existingResults])

  // Per-sample rerun traceability (K16): for a given sample id, the FORWARD link (this sample is
  // an original that has a rerun) and the REVERSE link (this sample IS a rerun of an original).
  // The cross-referenced sample's display label is the shared human sample number on the link
  // record (both ends carry it); no extra samples lookup is needed.
  const rerunFor = (sampleId) => ({
    forward: rerunLinks.find((r) => r.originalSampleId === sampleId),
    reverse: rerunLinks.find((r) => r.rerunSampleId === sampleId),
  })

  // Per-sample editable analyst state:
  //   { [sampleId]: {
  //       values: { [parameterId]: rawString },            // MEASURED parameter value cells
  //       inputs: { [parameterId]: { [inputKey]: rawString | rawString[] } }, // CALCULATED inputs
  //   } }
  // A calculated parameter's aggregate input holds an array of the n entered strings; every other
  // input holds a single raw string (Danish comma tolerated — normalised by the engine).
  const [entries, setEntries] = useState({})
  const [saving, setSaving] = useState(false)
  const [showQaModal, setShowQaModal] = useState(false)
  const [showReturnModal, setShowReturnModal] = useState(false)
  // True while the send-to-review gate-override e-signature dialog is shown (SPEC-021): ONE
  // reviewer override signature authorises whichever hard gate(s) block the send (expired/
  // discarded reference standard — SPEC-015 — and/or an unmet SST — SPEC-017). Its password
  // rides on the SAME re-issued send-to-review PATCH, so the server signs the deviation and
  // transitions atomically; the server is the authoritative gate (422), the client checks are
  // fail-fast UX.
  const [showOverrideGate, setShowOverrideGate] = useState(false)
  const [returnComment, setReturnComment] = useState("")
  // The in-review result pending an e-signed okOverride, or null.
  const [overriding, setOverriding] = useState(null)
  // Per-sample OOT comment drafts (reviewer), keyed by result id while editing.
  const [ootDrafts, setOotDrafts] = useState({})
  // RR2 (#10/Q2): the OOS investigation modal opened from a FAIL result here, or null.
  const [activeOosId, setActiveOosId] = useState(null)
  // SPEC-029 (RR4 #5): the in-review result open in the reviewer "Review" editor (override / OOT /
  // OOS), or null. The reviewer per-sample controls live in this editor now, not inline on the card.
  const [reviewEditId, setReviewEditId] = useState(null)
  // SPEC-031: the draft sample pending the "Discard (lost/destroyed)" e-signature, or null.
  const [discardingSample, setDiscardingSample] = useState(null)
  // F9 (SPEC-041): the sample whose sample-prep worklist popup is open, or null.
  const [worklistSample, setWorklistSample] = useState(null)
  // SPEC-035 (F3): the supervisor approve-to-QA e-signature dialog, the supervisor return-to-reviewer
  // modal, and that return's mandatory comment draft.
  const [showSupervisorModal, setShowSupervisorModal] = useState(false)
  const [showSupervisorReturnModal, setShowSupervisorReturnModal] = useState(false)
  const [supervisorReturnComment, setSupervisorReturnComment] = useState("")

  // Review state derived from the saved results.
  const inReviewResults = useMemo(
    () => existingResults.filter((r) => r.state === "in-review"),
    [existingResults]
  )
  const sentToQaCount = useMemo(
    () => existingResults.filter((r) => r.state === "sent-to-qa").length,
    [existingResults]
  )
  // The analyst(s) who entered the in-review results — excluded from signing the review (4-eyes).
  const enterers = useMemo(
    () => Array.from(new Set(inReviewResults.map((r) => r.enteredBy).filter(Boolean))),
    [inReviewResults]
  )
  // May the current user act as the reviewer here? (holds "reviewer" AND is not an enterer)
  const reviewGate = useSelector(
    (state) => canSign(state, { capability: "reviewer", excludedMemberIds: enterers }),
    shallowEqual
  )
  // SPEC-029 (RR4 #5): the QA act-gate, recomputed here (mirrors QaDecisionSection's excluded set —
  // the analyst enterer(s) + reviewer(s)) so the QA phase frame can reflect whether THIS user may
  // decide. QaDecisionSection still owns the authoritative gating of its own controls.
  const qaExcludedMemberIds = useMemo(() => {
    const set = new Set()
    existingResults.forEach((r) => {
      if (r.enteredBy) set.add(r.enteredBy)
      if (r.reviewerSignature && r.reviewerSignature.memberId) set.add(r.reviewerSignature.memberId)
      if (r.reviewedBy) set.add(r.reviewedBy)
    })
    return Array.from(set)
  }, [existingResults])
  const qaGate = useSelector(
    (state) => canSign(state, { capability: "qa", excludedMemberIds: qaExcludedMemberIds }),
    shallowEqual
  )
  // SPEC-035 (F3): results awaiting the supervisor (chemist) — the reviewer signed them on to the
  // supervisor. The supervisor phase table acts on these.
  const sentToSupervisorResults = useMemo(
    () => existingResults.filter((r) => r.state === "sent-to-supervisor"),
    [existingResults]
  )
  // 4-eyes for the supervisor: the supervisor must differ from the analyst enterer(s) AND the
  // technical reviewer(s) of the results being approved (an independent third pair of eyes).
  const supervisorExcludedMemberIds = useMemo(() => {
    const set = new Set()
    sentToSupervisorResults.forEach((r) => {
      if (r.enteredBy) set.add(r.enteredBy)
      if (r.reviewerSignature && r.reviewerSignature.memberId) set.add(r.reviewerSignature.memberId)
      if (r.reviewedBy) set.add(r.reviewedBy)
    })
    return Array.from(set)
  }, [sentToSupervisorResults])
  const supervisorGate = useSelector(
    (state) => canSign(state, { capability: "supervisor", excludedMemberIds: supervisorExcludedMemberIds }),
    shallowEqual
  )
  // The most recent "return for correction" comment, surfaced to the analyst on a returned draft.
  const returnedComment = useMemo(() => {
    const r = existingResults.find((x) => x.state === "draft" && x.reviewComment)
    return r ? r.reviewComment : null
  }, [existingResults])

  // RR2 (#10/Q2): open (or reopen) the OOS investigation for a sample whose result FAILed, from QC
  // Results & Approvals (analyst/reviewer). Reopen an existing open investigation, else create one
  // and surface its modal. The sample-row OOS badge reopens it elsewhere too. SPEC-037 (F5): the
  // same flow opens an OOT investigation (type "OOT") from an OOT-marked row — an OOT is the SAME
  // Phase-1 lab investigation, only barred from Phase 2 (the reason text + the reopen-existing
  // match differ by type, so a sample's OOS and OOT investigations are distinct records).
  const handleOpenInvestigation = (s, type = "OOS") => {
    if (!s || !s.id) return
    setReviewEditId(null) // close the reviewer editor before surfacing the modal (no stacked popups)
    const open = (allOos || []).find(
      (o) => o.sampleId === s.id && o.state !== "closed" && o.phase1 && (o.type || "OOS") === type
    )
    if (open) {
      setActiveOosId(open.id)
      return
    }
    // #4145 — DISPLAY, and the one that matters most: `sampleLabel` is PERSISTED on the OOS
    // record and rendered verbatim by the Investigations list, the OOS modal, both audit tables
    // and two toasts. A suffix baked in here would outlive the fix. Identity rides on `sampleId`,
    // which is sent alongside, so nothing keys on this string.
    const obsSuffix = s.observationId ? ` (${displaySampleId(s.observationId)})` : ""
    const sampleLabel = `${s.sampleName || ""}${obsSuffix}`
    dispatch(
      limsOos.openInvestigation({
        sampleId: s.id,
        analysisId,
        sampleLabel,
        analysisLabel: analysis ? analysis.name : "",
        type,
        reason: type === "OOT" ? t("limsResultOotAutoReason") : t("limsResultOosAutoReason"),
      })
    )
      .then((oos) => {
        if (oos && oos.id) setActiveOosId(oos.id)
        dispatch(message.success(t("limsOosOpened", { sample: sampleLabel })))
      })
      .catch(() => dispatch(message.error(t("limsOosOpenFailed", { sample: sampleLabel }))))
  }

  // The auto-fill resolver changes identity whenever the run's selected consumables change
  // (autoFillForSource is memoised on reagentLotIds). Capture its LATEST value in a ref so the seed
  // effect can use it WITHOUT taking it as a dependency — otherwise selecting/clearing a consumable
  // would re-run the seed below and wholesale-replace `entries`, wiping the analyst's in-progress,
  // not-yet-sent draft (RR7 verify follow-up #2 — e.g. typing HPLC calc inputs then picking a
  // reagent blanked the fields).
  const autoFillRef = useRef(autoFillForSource)
  useEffect(() => {
    autoFillRef.current = autoFillForSource
  }, [autoFillForSource])

  // Seed per-sample analyst state from any existing results. With sampleItems/resultBySample stable
  // (shallowEqual), this runs only when the underlying samples, saved results or spec parameters
  // actually change — never on a keystroke or a consumable-selection change — so in-progress edits
  // are preserved. The latest auto-fill resolver is read from the ref so a fresh seed still
  // pre-fills reference-standard-sourced inputs from the currently-selected consumables.
  useEffect(() => {
    const seeded = {}
    sampleItems.forEach((s) => {
      seeded[s.id] = buildSeededEntry(resultBySample[s.id], parameters, autoFillRef.current)
    })
    setEntries(seeded)
  }, [sampleItems, resultBySample, parameters])

  // R19: toggle the analyst OK on a parameter-less sample. Lives in the same per-sample entry
  // store as the values, so it follows the identical seed/reset lifecycle.
  const setSampleOk = (sampleId, ok) => {
    setEntries((prev) => {
      const cur = prev[sampleId] || { values: {}, inputs: {} }
      return { ...prev, [sampleId]: { ...cur, ok } }
    })
  }

  const setSampleValue = (sampleId, parameterId, raw) => {
    setEntries((prev) => {
      const cur = prev[sampleId] || { values: {}, inputs: {} }
      return { ...prev, [sampleId]: { ...cur, values: { ...cur.values, [parameterId]: raw } } }
    })
  }

  // Set one calculated-input value (scalar input). `inputKey` is the engine variable name.
  const setSampleInput = (sampleId, parameterId, inputKey, raw) => {
    setEntries((prev) => {
      const cur = prev[sampleId] || { values: {}, inputs: {} }
      const curInputs = cur.inputs || {}
      const curParam = curInputs[parameterId] || {}
      return {
        ...prev,
        [sampleId]: { ...cur, inputs: { ...curInputs, [parameterId]: { ...curParam, [inputKey]: raw } } },
      }
    })
  }

  // Replace the whole aggregate-list array for a calculated parameter's aggregate input.
  const setSampleAggregate = (sampleId, parameterId, inputKey, list) => {
    setEntries((prev) => {
      const cur = prev[sampleId] || { values: {}, inputs: {} }
      const curInputs = cur.inputs || {}
      const curParam = curInputs[parameterId] || {}
      return {
        ...prev,
        [sampleId]: { ...cur, inputs: { ...curInputs, [parameterId]: { ...curParam, [inputKey]: list } } },
      }
    })
  }

  const resultStateFor = (sampleId) => {
    const r = resultBySample[sampleId]
    return r ? r.state : null
  }
  // Locked = EVERY post-draft state (SPEC-020 BLOCKER fix): in-review, sent-to-qa (incl. the
  // legacy "reviewed" it migrates from) AND the QA-decided terminal states qa-approved / qa-rerun /
  // qa-stopped. A QA-disposed record must not be editable nor re-sendable — re-sending would reset
  // it to draft and silently destroy the reviewer/QA signatures (Part 11). The ONLY controlled way
  // back to draft is the reviewer's "Return for correction".
  // #4111: entering results is the ANALYST's work. Without the capability the grid is read-only —
  // the same treatment a sent-on result gets, so a reviewer or QA opening the analysis still reads
  // everything and simply cannot type into it. The server refuses either way (POST /lims/results);
  // this is the fail-fast half, so the panel does not offer work that would 403 on save.
  const canEnterResults = useSelector(
    (state) => isGmpRelaxed(state) || hasQcCapability(state, state.auth.memberId, "analyst")
  )
  // #4142: the two halves of "locked" pulled apart. `isStateLocked` is where the RESULT stands —
  // pure state, identical for every viewer; `isLocked` adds "…or you are not an analyst", which is
  // about the VIEWER. Editability needs both (unchanged); the phase highlight needs the state half
  // only, or which step lights up starts depending on who is looking (see the activePhase block).
  const isStateLocked = (sampleId) => {
    const s = resultStateFor(sampleId)
    return s !== null && s !== "draft"
  }
  const isLocked = (sampleId) => !canEnterResults || isStateLocked(sampleId)

  // Live per-parameter judgement for one sample. MEASURED parameters judge the entered value;
  // CALCULATED parameters run the engine over the entered inputs (calc-aware pass/computed value).
  // The spec context ({ parameters, entries }) lets the engine resolve parameter-sourced inputs
  // from sibling parameters (SPEC-023) — chains re-judge live as source values are typed.
  const judgeParam = (sampleId, p) => {
    const entry = entries[sampleId] || { values: {}, inputs: {} }
    if (limsSpecs.isCalculatedParameter(p)) {
      return limsResults.judgeCalculatedParameter(p, (entry.inputs && entry.inputs[p.id]) || {}, {
        parameters,
        entries: entry,
      })
    }
    return { pass: limsResults.judgeValue(p, entry.values[p.id]), computedValue: null, rounded: null, error: null }
  }

  // SPEC-023: the display name of a referenced source parameter (for the "from parameter X"
  // note on a read-only parameter-bound input).
  const sourceNameOf = (parameterId) => {
    const source = parameters.find((p) => p.id === parameterId)
    return source && source.name ? source.name : "?"
  }

  // Raw computed overall judgement from the current analyst input (vs the spec — an analyst OK can
  // never overrule a judged parameter). Spans measured + calculated parameters.
  //
  // R20 (#4084): with NO parameters there is nothing to judge, so the analyst's OK IS the verdict.
  // It stays null until the OK is given — a PASS badge on an un-attested sample would claim a
  // judgement nobody made. Once saved, the server records the same PASS (SPEC-056 R20).
  const judgeSample = (sampleId) => {
    if (parameters.length === 0) return sampleIsOk(sampleId) ? "PASS" : null
    const perParam = parameters.map((p) => judgeParam(sampleId, p))
    return limsResults.computeOverall(perParam.map((r) => ({ pass: r.pass })))
  }

  // A calculated input has content when its scalar is non-blank or its aggregate list has ≥1 entry.
  const calcParamHasContent = (entry, p) => {
    const paramInputs = (entry.inputs && entry.inputs[p.id]) || {}
    return Object.values(paramInputs).some((v) =>
      Array.isArray(v) ? v.some((x) => x !== "" && x != null) : v !== "" && v != null
    )
  }

  // R19 (#4084): whether the analyst has attested a parameter-less sample. Seeded from the saved
  // record (its existence IS the attestation — the same rule the server applies), so a reload or a
  // returned-for-correction draft keeps the OK.
  const sampleIsOk = (sampleId) => !!(entries[sampleId] && entries[sampleId].ok)

  // R19: with no parameters there are no values to fill, so the entered-value test below is always
  // false and `anySendable` could never become true — "Send to review" was unreachable on a method
  // without a specification. The analyst's OK is the second path to having content.
  const sampleHasContent = (sampleId) => {
    const entry = entries[sampleId] || { values: {}, inputs: {} }
    if (parameters.length === 0) return sampleIsOk(sampleId)
    const measuredFilled = Object.values(entry.values || {}).some((v) => v !== "" && v !== null && v !== undefined)
    const calcFilled = parameters.some((p) => limsSpecs.isCalculatedParameter(p) && calcParamHasContent(entry, p))
    return measuredFilled || calcFilled
  }

  // SPEC-031: a sample with a rerun link is being re-run (a QA may-reanalyse, or a discarded
  // lost/destroyed sample) — it must neither block the send-to-review gate nor be sent itself.
  const hasRerunLink = (sampleId) => rerunLinks.some((l) => l.originalSampleId === sampleId)

  // SPEC-030 (RR4 #6): samples that still need a result before the analysis can go to review — every
  // sample except FAILED (planner-rejected, never gets a QC result), already-sent (locked) ones, and
  // discarded/re-run ones (SPEC-031). Send-to-review is a single all-samples handoff, gated until empty.
  const getIncompleteSamples = () =>
    sampleItems.filter(
      (s) => s.status !== "FAILED" && !isLocked(s.id) && !sampleHasContent(s.id) && !hasRerunLink(s.id)
    )

  // Build the record to persist for one sample. No analyst OK: every parameter is judged vs spec.
  // A CALCULATED parameter is computed + snapshotted by the engine (buildCalculatedValue captures
  // the entered inputs + formula + full-precision computed value); a MEASURED parameter is judged
  // directly from its entered value, exactly as before.
  const buildRecord = (s) => {
    const entry = entries[s.id] || { values: {}, inputs: {} }
    const existing = resultBySample[s.id]
    const values = parameters.map((param) => {
      if (limsSpecs.isCalculatedParameter(param)) {
        // SPEC-023: the spec context resolves parameter-sourced inputs, and the snapshotted
        // `inputs` carry the RESOLVED sibling values (GMP reproducibility).
        return limsResults.buildCalculatedValue(param, (entry.inputs && entry.inputs[param.id]) || {}, {
          parameters,
          entries: entry,
        })
      }
      const raw = entry.values[param.id]
      const numeric = limsResults.toNumber(raw)
      return {
        parameterId: param.id,
        name: param.name,
        unit: param.unit,
        criterionType: param.criterionType,
        min: param.min ?? null,
        max: param.max ?? null,
        value: Number.isNaN(numeric) ? null : numeric,
        pass: limsResults.judgeValue(param, raw),
      }
    })
    return {
      id: existing ? existing.id : undefined,
      analysisId,
      analysisDefinitionId: analysisDefinitionId || null,
      sampleId: s.id,
      specSnapshot: { parameters, formula },
      values,
      reagentLotIds,
      instrumentId: primaryAssetId || null,
      // R20: pass the spec so a parameter-less record carries the PASS the server will record
      // (SPEC-056) instead of a null the UI would briefly render as "not yet judged". The server
      // recomputes it authoritatively either way — this only keeps the optimistic record truthful.
      overall: limsResults.computeOverall(values, parameters),
      enteredBy: existing ? existing.enteredBy : undefined,
      enteredAt: existing ? existing.enteredAt : undefined,
    }
  }

  // Persist one sample's result and send it to review (the server recomputes the judged fields
  // and enforces the hard gates). A computed Fail is an OOS by definition; SPEC-011 NO LONGER
  // flags the sample or raises a separate OOS record here — disposition happens downstream
  // (reviewer override / QA decision). `override` is the reviewer gate-override e-signature
  // whose password rides on the SAME send-to-review PATCH.
  const persistAndSend = (s, override) =>
    dispatch(limsResults.saveDraft(buildRecord(s))).then((saved) =>
      dispatch(limsResults.sendToReview(saved.id, override, taskLabels)).then(() => saved)
    )

  // Synchronous re-entry guard for the send flow (SPEC-020): `saving` is React state and updates
  // asynchronously, so a double-click on "Send to review" could start two overlapping send batches
  // (each POSTing its own record). The ref flips synchronously, making the second click a no-op.
  const sendingRef = useRef(false)

  // Whether an error is the server-side send-to-review gate rejection (HTTP 422 with the
  // distinct gate message). The SERVER is the authoritative gate; this detection lets the FE
  // open the override dialog even when its own fail-fast checks saw nothing (data drift).
  const isGateBlockError = (err) =>
    err instanceof InvalidParametersError && typeof err.message === "string" && err.message.includes("Send-to-review blocked")

  // Persist + send every sendable sample, then surface the analysis as plannable review work.
  // Factored out of handleSendAll so the gate-override signature can resume it: with `override`
  // set, each send-to-review PATCH carries the override password + reason and the server signs
  // the deviation (audited per overridden gate) and transitions atomically. Returns the promise
  // so the override dialog can stay open on a 403 (wrong password / capability / 4-eyes).
  // SPEC-062 (#4135) — the SERVER now creates and clears the QC tasks, inside the phase
  // transition's own transaction. We only supply the translated names: the backend has no
  // translation layer, and a plan action's name is displayed raw. Sending them is also what tells
  // the server this client no longer manages the tasks itself.
  const taskLabels = limsReviews.qcTaskLabels(analysis && analysis.name)

  const performSend = (override = null) => {
    if (sendingRef.current) return Promise.resolve()
    const toSend = sampleItems.filter((s) => sampleHasContent(s.id) && !isLocked(s.id) && !hasRerunLink(s.id))
    if (toSend.length === 0) return Promise.resolve()
    sendingRef.current = true
    setSaving(true)
    return Promise.all(toSend.map((s) => persistAndSend(s, override)))
      .then(() => {
        setShowOverrideGate(false)
        // SPEC-062: the review task was created by the transition itself, server-side.
        dispatch(message.success(t("limsResultSentToReview")))
      })
      .catch((err) => {
        // The server rejected on a hard gate (it is authoritative): open the override dialog —
        // unless this WAS the override attempt, in which case rethrow so the dialog shows it.
        if (!override && isGateBlockError(err)) {
          dispatch(message.warning(err.message))
          setShowOverrideGate(true)
          return
        }
        if (override) throw err
        dispatch(message.error((err && err.message) || t("limsResultSaveFailed")))
      })
      .finally(() => {
        sendingRef.current = false
        setSaving(false)
      })
  }

  // #4103/#4107: persist what has been entered WITHOUT sending it on. `saveDraft` was previously
  // reachable only from inside persistAndSend, so results lived in component state until the whole
  // analysis was complete enough to go to review — close the modal with one sample still to run and
  // the typing was gone, with nothing on the panel offering to keep it (the modal's own Save button
  // saves the analysis TASK, which is what made it look as though results should already be safe).
  //
  // Deliberately NOT gated on the send preconditions — every sample complete, consumables covered,
  // SST passed. A draft is meant to be incomplete; that is the whole point of having one.
  const savingDraftRef = useRef(false)
  const handleSaveDraft = () => {
    if (savingDraftRef.current) return
    const toSave = sampleItems.filter((s) => sampleHasContent(s.id) && !isLocked(s.id) && !hasRerunLink(s.id))
    if (toSave.length === 0) return
    savingDraftRef.current = true
    setSaving(true)
    Promise.all(toSave.map((s) => dispatch(limsResults.saveDraft(buildRecord(s)))))
      .then(() => dispatch(message.success(t("limsResultDraftSaved"))))
      .catch((err) => dispatch(message.error((err && err.message) || t("limsResultSaveFailed"))))
      .finally(() => {
        savingDraftRef.current = false
        setSaving(false)
      })
  }

  // Send to review — the deliberate GMP handoff to second-person review. HARD GATES, evaluated
  // fail-fast from local state (SPEC-015 expired/discarded reference standard; SPEC-017 unmet
  // SST) and authoritatively by the server (422): when a gate blocks, ONE reviewer override
  // e-signature (4-eyes) authorises whichever gate(s) blocked and rides on the re-issued send.
  // Quarantine / expiring-soon are warnings only (rendered below) and never block.
  const handleSendAll = () => {
    if (sendingRef.current) return // double-click guard (SPEC-020) — see performSend
    const toSend = sampleItems.filter((s) => sampleHasContent(s.id) && !isLocked(s.id) && !hasRerunLink(s.id))
    if (toSend.length === 0) return
    // SPEC-030 (RR4 #6): the whole analysis goes to review together — block until every sample has a
    // result (the button is disabled too; this is the defensive backstop).
    const missing = getIncompleteSamples().length
    if (missing > 0) {
      dispatch(message.warning(t("limsResultSendAllIncomplete", { remaining: missing })))
      return
    }
    // F8 (SPEC-040): every consumable requirement must be covered by a selected consumable before
    // the analysis can go to review. NOT an e-sign-overridable deviation (it is a completeness
    // check) — block with a fail-fast hint; the server is the authoritative gate (422).
    if (uncoveredRequirements.length > 0) {
      dispatch(message.warning(t("limsResultSendConsumablesIncomplete", { remaining: uncoveredRequirements.length })))
      return
    }
    if (blockingStandards.length > 0 || (requiresSst && !sstReady)) {
      setShowOverrideGate(true)
      return
    }
    performSend()
  }

  // The gate override was e-signed by a qualified non-enterer: re-issue the send with the
  // override signature's password + reason on the SAME call. The server verifies the signer,
  // writes the deviation signature + one audit entry per overridden gate, and transitions.
  const handleGateOverrideSign = (signature) =>
    performSend({ password: signature.password, reason: signature.reason })

  // Reviewer SEND-TO-QA: the reviewer e-signs off every in-review result for this analysis. SPEC-035
  // (F3): when the analysis requires a supervisor review the target is the SUPERVISOR (sendToSupervisor
  // → "sent-to-supervisor"), and a "Review Supervisor <analysis>" task is created; otherwise it is the
  // unchanged hand-off straight to QA (sendToQa → "sent-to-qa"). Either way the now-stale unplanned
  // "review" task is cleared so the inbox does not hoard tiles. Returns the promise so the dialog
  // surfaces a backend 403 inline.
  // F9 (SPEC-041): the in-review results whose sample-prep worklist is REQUIRED (this analysis has
  // an effective worklist template) but NOT signed — the reviewer hand-off is blocked until empty.
  // Fail-fast mirror of the server gate (results.py 409); the server stays authoritative.
  const worklistUnsignedFor = (results) => {
    if (!hasWorklistTemplate) return []
    return results.filter(
      (r) => !sampleWorklists.some((w) => w.sampleId === r.sampleId && w.analysisId === r.analysisId && w.state === "signed")
    )
  }

  const handleSendToQaSign = (signature) => {
    // F9: block the hand-off when any in-review sample still lacks a signed worklist.
    const missing = worklistUnsignedFor(inReviewResults)
    if (missing.length > 0) {
      dispatch(message.warning(t("limsWorklistGateBlocked", { count: missing.length })))
      return Promise.resolve()
    }
    setSaving(true)
    const sendOne = requiresSupervisorReview
      ? (r) => dispatch(limsResults.sendToSupervisor(r.id, signature, taskLabels))
      : (r) => dispatch(limsResults.sendToQa(r.id, signature, taskLabels))
    return Promise.all(inReviewResults.map(sendOne))
      .then(() => {
        setShowQaModal(false)
        // SPEC-062: the transition opened the supervisor's task and closed the reviewer's, in one
        // transaction. This used to be two more browser round-trips, the second of which is what
        // produced #4135 for a reviewer.
        dispatch(
          message.success(
            requiresSupervisorReview ? t("limsResultSentToSupervisorMsg") : t("limsResultSentToQaMsg")
          )
        )
      })
      .finally(() => setSaving(false))
  }

  // SPEC-035 (F3) SUPERVISOR APPROVE: the supervisor e-signs every sent-to-supervisor result on to
  // QA (→ "sent-to-qa") and the now-stale unplanned "supervisor-review" task is cleared. Returns the
  // promise so the dialog surfaces a backend 403 inline.
  const handleSupervisorApproveSign = (signature) => {
    // F9 (SPEC-041): defence-in-depth — the worklist was already required at the reviewer's
    // send-to-supervisor; block here too if any sent-to-supervisor sample lacks a signed worklist.
    const missing = worklistUnsignedFor(sentToSupervisorResults)
    if (missing.length > 0) {
      dispatch(message.warning(t("limsWorklistGateBlocked", { count: missing.length })))
      return Promise.resolve()
    }
    setSaving(true)
    return Promise.all(sentToSupervisorResults.map((r) => dispatch(limsResults.supervisorApprove(r.id, signature, taskLabels))))
      .then(() => {
        setShowSupervisorModal(false)
        // SPEC-062: the transition closed the supervisor's task.
        dispatch(message.success(t("limsResultSentToQaMsg")))
      })
      .finally(() => setSaving(false))
  }

  // SPEC-035 (F3) SUPERVISOR RETURN: the supervisor sends every sent-to-supervisor result back to the
  // TECHNICAL reviewer (→ "in-review") with a MANDATORY comment. Not e-signed. The supervisor-review
  // task is the supervisor's; the result is now back in the reviewer's court, so leave the task as-is
  // (the reviewer re-sends, re-using/refreshing it). A "Review <analysis>" task is re-ensured so the
  // reviewer sees the work again.
  const handleSupervisorReturnSubmit = () => {
    const comment = supervisorReturnComment.trim()
    if (!comment) return
    setShowSupervisorReturnModal(false)
    setSaving(true)
    // SPEC-062: the transition re-opens the reviewer's task server-side.
    Promise.all(
      sentToSupervisorResults.map((r) =>
        dispatch(limsResults.supervisorReturnForReview(r.id, comment, taskLabels))
      )
    )
      .then(() => dispatch(message.success(t("limsSupervisorReturned"))))
      .catch(() => dispatch(message.error(t("limsResultSaveFailed"))))
      .finally(() => {
        setSaving(false)
        setSupervisorReturnComment("")
      })
  }

  // Reviewer RETURN: send every in-review result back to the analyst for correction (→ "draft")
  // and raise a "Correct <analysis>" task for the analyst (assigned to their competence group).
  const handleReturnSubmit = () => {
    const comment = returnComment.trim()
    if (!comment) return
    setShowReturnModal(false)
    setSaving(true)
    // SPEC-062: the transition opens the correction task, assigned to the analyst who entered the
    // result — the server reads that from the record rather than being told.
    Promise.all(
      inReviewResults.map((r) => dispatch(limsResults.returnForCorrection(r.id, comment, taskLabels)))
    )
      .then(() => dispatch(message.success(t("limsResultReviewReturned"))))
      .catch(() => dispatch(message.error(t("limsResultSaveFailed"))))
      .finally(() => {
        setSaving(false)
        setReturnComment("")
      })
  }

  // Reviewer per-sample OK OVERRIDE. An override that flips a computed Fail to an effective Pass is
  // a disposition decision and is E-SIGNED (reviewer capability + 4-eyes, gathered by
  // ESignatureModal). Clearing an override is not e-signed.
  const handleSetOverride = (record, next) => {
    if (next && record.overall === "FAIL") {
      setReviewEditId(null) // close the reviewer editor so the e-sign dialog isn't stacked on it
      setOverriding(record) // e-sign first
      return
    }
    setSaving(true)
    dispatch(limsResults.setOverride(record.id, next, null))
      .then(() => dispatch(message.success(t("limsResultOverrideSaved"))))
      .catch(() => dispatch(message.error(t("limsResultSaveFailed"))))
      .finally(() => setSaving(false))
  }
  // The okOverride e-signature: returns the promise so the dialog shows a backend 403 inline
  // (wrong password / not a reviewer / 4-eyes) and closes only on success.
  const handleOverrideSign = (signature) => {
    const record = overriding
    if (!record) return undefined
    setSaving(true)
    return dispatch(limsResults.setOverride(record.id, true, signature))
      .then(() => {
        setOverriding(null)
        dispatch(message.success(t("limsResultOverrideSaved")))
      })
      .finally(() => setSaving(false))
  }

  // Reviewer per-sample OUT-OF-TREND toggle + comment (informational, not e-signed).
  const handleSetOot = (record, next) => {
    setSaving(true)
    const comment = next ? ootDrafts[record.id] ?? record.ootComment ?? "" : null
    dispatch(limsResults.setOot(record.id, next, comment))
      .then(() => dispatch(message.success(t("limsResultOotSaved"))))
      .catch(() => dispatch(message.error(t("limsResultSaveFailed"))))
      .finally(() => setSaving(false))
  }
  const commitOotComment = (record) => {
    const draft = ootDrafts[record.id]
    if (draft === undefined || draft === (record.ootComment ?? "")) return
    setSaving(true)
    dispatch(limsResults.setOot(record.id, true, draft))
      .catch(() => dispatch(message.error(t("limsResultSaveFailed"))))
      .finally(() => setSaving(false))
  }

  // SPEC-031: discard a draft sample physically lost/destroyed during analysis. An e-signed deviation
  // (mandatory free-text reason) that spawns a re-run (createRerun, authorised by the discard signature
  // — NOT a QA decision). The sample keeps its record + shows its "Rerun created" badge, and the
  // send-to-review gate then excludes it (it no longer needs a result on this analysis).
  const handleDiscardSign = (signature) => {
    const s = discardingSample
    if (!s) return undefined
    setSaving(true)
    return dispatch(
      limsReruns.createRerun({
        // SPEC-054: the server derives the rerun sample from the original, so only its identity
        // is needed here — no analysisIds (the old fallback sent the RUN id and 419'd), no dates.
        originalSample: {
          id: s.id,
          sampleName: s.sampleName || "",
          productName: s.productName || null,
        },
        analysisId,
        viaDiscard: true,
        signature,
      })
    )
      .then(() => {
        setDiscardingSample(null)
        dispatch(message.success(t("limsDiscardDone", { sample: s.sampleName || "" })))
      })
      .finally(() => setSaving(false))
  }

  if (!analysisId) {
    return (
      <div className="ResultEntryPanel">
        <p className="ResultEntryPanel-message">{t("limsResultNoSpec")}</p>
      </div>
    )
  }

  const anySendable = sampleItems.some((s) => sampleHasContent(s.id) && !isLocked(s.id) && !hasRerunLink(s.id))
  // #4142: the same question asked of the RESULTS alone — is there analyst-stage work sitting in
  // this run? True for a reviewer or QA looking at an analyst's drafts, where `anySendable` is
  // false simply because they are not the analyst. Drives the highlight and whether the analyst's
  // actions render (ghosted) — never whether they are enabled, which stays `anySendable`.
  const anyAnalystWork = sampleItems.some(
    (s) => sampleHasContent(s.id) && !isStateLocked(s.id) && !hasRerunLink(s.id)
  )
  // #4142: why the analyst controls are ghosted for this viewer. Reuses the message #4111 already
  // shows at the top of the phase, so the ghosting and the explanation are the same sentence.
  const analystGhostHint = canEnterResults ? null : t("limsResultNeedsAnalystCapability")
  // Are there in-review results to review at all? (drives whether the reviewer controls render —
  // for EVERYONE, ghosted for non-qualified viewers, per GMP/ALCOA+: all can SEE, only the
  // qualified can ACT — SPEC-022 F2 / K16). The previous `reviewerActive` (results AND allowed)
  // is replaced by this visibility gate + the reviewGate.allowed act gate.
  const hasInReview = inReviewResults.length > 0
  // One-line explanation for a viewer whose reviewer controls are ghosted: four-eyes
  // (entered these results) vs missing reviewer capability — mirrors the QA section's hint.
  let reviewerGhostHint = null
  if (!reviewGate.allowed) {
    reviewerGhostHint =
      reviewGate.reasonKey === "limsEsigBlockedSelf"
        ? t("limsReviewGhostFourEyes")
        : t("limsReviewGhostCapability")
  }

  // SPEC-035 (F3): is there supervisor work, and a ghost hint for a viewer who cannot supervise.
  const hasSentToSupervisor = sentToSupervisorResults.length > 0
  let supervisorGhostHint = null
  if (!supervisorGate.allowed) {
    supervisorGhostHint =
      supervisorGate.reasonKey === "limsEsigBlockedSelf"
        ? t("limsSupervisorGhostFourEyes")
        : t("limsSupervisorGhostCapability")
  }

  // SPEC-029 (RR4 #5), rewritten for #4142: the highlight says WHERE THE WORK IS, and that is a
  // property of the run, not of the reader — so every viewer sees the same step lit up, whatever
  // role they hold. Earliest actionable stage wins (Analyst → Reviewer → [Supervisor] → QA — the
  // supervisor phase only exists when the analysis requires it, SPEC-035).
  //
  // These four flags used to read `hasSentToSupervisor && supervisorGate.allowed` etc., mixing the
  // run's state with the viewer's capability, which is exactly what the customer rejected after
  // testing: "den aktive fase er highlightet for alle, men inputfelter ghostes for den forkerte
  // rolle". Capability now lives on the OTHER axis only — see the `*ControlsGhosted` values below.
  const analystStage = anyAnalystWork
  // SPEC-030 (RR4 #6): "Send to review" is enabled only when EVERY sample has a result. F8
  // (SPEC-040): AND every consumable requirement is covered by a selected consumable.
  const incompleteCount = getIncompleteSamples().length
  const uncoveredCount = uncoveredRequirements.length
  // R7 (RR8 #4059): a SIGNED sample-prep worklist gates send-to-review too. The precondition already
  // existed but was only enforced from the reviewer hand-off onwards (and in the release check), so
  // the analyst could send results on with the prep document unsigned and the block appeared a step
  // late. Fail-fast mirror of the server gate (409, backend SPEC-052 R7); the server stays
  // authoritative. `worklistUnsignedFor` returns [] when the definition has no SPECIFIC effective
  // template, so analyses without their own worklist template are never blanket-gated.
  const worklistUnsignedCount = worklistUnsignedFor(
    sampleItems
      .filter((s) => sampleHasContent(s.id) && !isLocked(s.id) && !hasRerunLink(s.id))
      .map((s) => ({ sampleId: s.id, analysisId }))
  ).length
  const allSamplesReady =
    anySendable && incompleteCount === 0 && uncoveredCount === 0 && worklistUnsignedCount === 0
  // The fail-fast hint for the disabled send button: samples-missing takes priority (it is the
  // primary workflow blocker), then the consumable-requirements completeness gate, then the
  // worklist signature (R8). Reuses the existing gate message so the hand-off and the send-to-review
  // block read identically — it is the same precondition.
  let sendBlockedHint = null
  if (incompleteCount > 0) sendBlockedHint = t("limsResultSendAllIncomplete", { remaining: incompleteCount })
  else if (uncoveredCount > 0) sendBlockedHint = t("limsResultSendConsumablesIncomplete", { remaining: uncoveredCount })
  else if (worklistUnsignedCount > 0) sendBlockedHint = t("limsWorklistGateBlocked", { count: worklistUnsignedCount })
  const reviewerStage = hasInReview
  const supervisorStage = hasSentToSupervisor
  const qaStage = sentToQaCount > 0
  const hasQaStage = sentToQaCount > 0 || existingResults.some((r) => limsResults.isQaDecided(r))
  // #4144 — has anything left the analyst's hands? Deliberately a STATE question with no
  // capability in it — like every flag above now is: it decides whether "nothing entered yet" is
  // still a truthful description of the run, and that does not depend on who is looking.
  const anyResultPastDraft = existingResults.some((r) => r.state && r.state !== "draft")
  let activePhase = null
  if (analystStage) activePhase = "analyst"
  else if (reviewerStage) activePhase = "reviewer"
  else if (supervisorStage) activePhase = "supervisor"
  else if (qaStage) activePhase = "qa"
  // R6 (RR8 #4051): `analystStage` needs something actually entered, so an analysis with nothing
  // entered yet had NO active phase and every step rendered ghosted — including the one the analyst
  // is actually on. The ghosting itself is correct; the missing default was the bug. Excluded once
  // the run has reached a QA stage: highlighting step 1 on a finished analysis would imply there is
  // work left to do.
  //
  // #4144: `hasQaStage` alone was too narrow a brake. Every stage flag above USED to be a
  // CAPABILITY gate as well as a state check (`hasSentToSupervisor && supervisorGate.allowed`), so
  // a result sitting at the supervisor step, viewed by someone without the supervisor capability,
  // made all four false — and this fallback then highlighted step 1, pointing the reader at the
  // analyst while the work was two stages further on. #4142 removed the capability half outright,
  // which fixes that at the root; the default keeps its actual purpose (nothing entered yet) and
  // both guards stay, so a corrected run whose drafts were all returned still reads truthfully.
  else if (!hasQaStage && !anyResultPastDraft) activePhase = "analyst"
  // #4142 — the SECOND axis. Ghosting is no longer the inverse of the highlight: a non-active phase
  // ghosts WHOLE (the work is not here), while the active phase is never section-ghosted and
  // instead ghosts just its interactive controls when the viewer lacks that phase's role (the work
  // is here, but not yours to do). Headings, hints and result tables therefore stay at full
  // strength on the active phase for every reader. Each hint below already existed as the
  // per-control act-gate explanation — the same sentence now also says why the controls are dim.
  //
  // A FINISHED run (every sample QA-decided) leaves activePhase null, so nothing is highlighted and
  // every section ghosts whole — no controls-ghosting anywhere, because there is nothing to act on.
  const analystControlsGhosted = activePhase === "analyst" && !canEnterResults
  const reviewerControlsGhosted = activePhase === "reviewer" && !reviewGate.allowed
  const supervisorControlsGhosted = activePhase === "supervisor" && !supervisorGate.allowed
  const qaControlsGhosted = activePhase === "qa" && !qaGate.allowed
  // SPEC-035 (F3): the per-row "Review" editor serves BOTH the reviewer (in-review) and supervisor
  // (sent-to-supervisor) phases — resolve the open record from either, and use the phase-appropriate
  // act-gate (supervisor while sent-to-supervisor, reviewer while in-review) for its controls.
  const reviewEditRecord = reviewEditId
    ? inReviewResults.find((r) => r.id === reviewEditId) ||
      sentToSupervisorResults.find((r) => r.id === reviewEditId) ||
      null
    : null
  const reviewEditIsSupervisor = !!(reviewEditRecord && reviewEditRecord.state === "sent-to-supervisor")
  const reviewEditGate = reviewEditIsSupervisor ? supervisorGate : reviewGate
  const reviewEditGhostHint = reviewEditIsSupervisor ? supervisorGhostHint : reviewerGhostHint
  const reviewEditSample = reviewEditRecord
    ? sampleItems.find((x) => x.id === reviewEditRecord.sampleId) || {}
    : null

  return (
    <div className="ResultEntryPanel">
      {returnedComment && (
        <div className="ResultEntryPanel-returned">
          <strong>{t("limsResultReturnedLabel")}:</strong> {returnedComment}
        </div>
      )}

      {/* SPEC-029 (RR4 #5): ANALYST phase — the editable per-sample entry grid + Send to review. */}
      <PhaseSection
        title={t("limsPhaseAnalyst")}
        stepLabel="1"
        active={activePhase === "analyst"}
        controlsGhosted={analystControlsGhosted}
      >
      {/* #4111: say WHY the grid is read-only, rather than letting it look broken. #4142: this IS
          the analyst phase's ghost reason — it sits right under the header, so no header hint is
          passed above (the reviewer/supervisor phases put theirs in the header because their own
          explanation is far below, under their table). */}
      {!canEnterResults && (
        <p className="ResultEntryPanel-message">{t("limsResultNeedsAnalystCapability")}</p>
      )}
      {sampleItems.length === 0 && (
        <p className="ResultEntryPanel-message">{t("limsResultNoSamples")}</p>
      )}

      {sampleItems.length > 0 && parameters.length === 0 && (
        <p className="ResultEntryPanel-message">{t("limsResultNoParams")}</p>
      )}

      {sampleItems.length > 0 && (
        <div className="ResultEntryPanel-samples">
          {sampleItems.map((s) => {
            const entry = entries[s.id] || { values: {}, inputs: {} }
            const record = resultBySample[s.id]
            const locked = isLocked(s.id)
            // Locked samples show the persisted raw computed judgement; editable ones judge live.
            const raw = locked && record ? record.overall : judgeSample(s.id)
            const links = rerunFor(s.id)
            return (
              <section key={s.id} className="ResultEntryPanel-sample">
                <div className="ResultEntryPanel-sample-head">
                  <div className="ResultEntryPanel-sample-id">
                    <span className="ResultEntryPanel-sample-name">{s.sampleName}</span>
                    {s.observationId && (
                      <>
                        <span className="ResultEntryPanel-sample-sep">·</span>
                        <span className="ResultEntryPanel-sample-obs">{displaySampleId(s.observationId)}</span>
                      </>
                    )}
                    <span className="ResultEntryPanel-sample-sep">·</span>
                    <span className="ResultEntryPanel-sample-product">{s.productName || t("none")}</span>
                    {/* K16 — rerun traceability, both directions, on the sample card. */}
                    {links.forward && (
                      <StatusBadge
                        variant="info"
                        className="ResultEntryPanel-rerun-link"
                        title={t("limsRerunCreatedToBadge", { sample: links.forward.sampleNumber || "" })}
                      >
                        {t("limsRerunCreatedToBadge", { sample: links.forward.sampleNumber || "" })}
                      </StatusBadge>
                    )}
                    {links.reverse && (
                      <StatusBadge
                        variant="info"
                        className="ResultEntryPanel-rerun-link"
                        title={t("limsRerunOfBadge", { sample: links.reverse.sampleNumber || "" })}
                      >
                        {t("limsRerunOfBadge", { sample: links.reverse.sampleNumber || "" })}
                      </StatusBadge>
                    )}
                  </div>
                  <div className="ResultEntryPanel-sample-judge">
                    <SampleStatusChain raw={raw} record={record} t={t} />
                  </div>
                </div>

                {/* SPEC-020: a LOCKED record renders its parameter rows from its OWN specSnapshot
                    + persisted values (the spec it was actually judged against — the live spec may
                    have changed since; the QA decisions section reads from the same source).
                    Editable drafts render the live spec's input grid as before. */}
                {locked && record && <LockedRecordRows record={record} t={t} />}

                {/* R19/R22 (#4084): a method with NO specification parameters has no input grid, so
                    the card used to render nothing at all while the message above it told the
                    analyst to "mark OK" — an instruction for a control that did not exist. The OK
                    IS the content of such a sample: it activates "Send to review" and the server
                    judges the saved result PASS (SPEC-056 R20). Disabled once a rerun supersedes
                    the sample, exactly like the parameter inputs. */}
                {!(locked && record) && parameters.length === 0 && (
                  <label className="ResultEntryPanel-analyst-ok">
                    <Checkbox
                      checked={sampleIsOk(s.id)}
                      onChange={(v) => setSampleOk(s.id, v)}
                      disabled={!!links.forward}
                    />
                    <span className="ResultEntryPanel-analyst-ok-label">{t("limsResultAnalystOk")}</span>
                  </label>
                )}

                {/* #4142 (SPEC-059): `locked` reaches the inputs below, not just this branch. #4111
                    made the grid "read-only" for a non-analyst, but only gated the ACTIONS and the
                    switch to LockedRecordRows — and that switch needs a saved `record`, so a
                    sample with no draft yet fell through to here with live inputs. A reviewer could
                    type a full set of values and only then find that nothing would save. */}
                {!(locked && record) && parameters.length > 0 && (
                  <table className="ResultEntryPanel-sample-grid">
                    <tbody>
                      {parameters.map((p) =>
                        limsSpecs.isCalculatedParameter(p) ? (
                          <CalculatedInputRows
                            key={p.id}
                            param={p}
                            inputValues={(entry.inputs && entry.inputs[p.id]) || {}}
                            judged={judgeParam(s.id, p)}
                            sourceNameOf={sourceNameOf}
                            disabled={locked || !!links.forward}
                            onScalarChange={(key, v) => setSampleInput(s.id, p.id, key, v)}
                            onAggregateChange={(key, list) => setSampleAggregate(s.id, p.id, key, list)}
                            t={t}
                          />
                        ) : (
                          <tr key={p.id}>
                            <th className="ResultEntryPanel-grid-label">
                              {p.name}
                              {p.unit ? ` (${p.unit})` : ""}
                              <span className="ResultEntryPanel-type"> · {t("limsSpecKindMeasured")}</span>
                              <div className="ResultEntryPanel-criterion">{criterionText(p, t)}</div>
                            </th>
                            <td>
                              <Text
                                value={entry.values[p.id] === undefined || entry.values[p.id] === null ? "" : String(entry.values[p.id])}
                                onChange={(v) => setSampleValue(s.id, p.id, v)}
                                disabled={locked || !!links.forward}
                              />
                            </td>
                          </tr>
                        )
                      )}
                    </tbody>
                  </table>
                )}

                {/* RR8 (obs.3): a card footer row — the "Kassér (lost/destroyed)" deviation action
                    sits left, the sample-prep worklist link + signed badge sit bottom-right
                    (margin-left:auto), the PlannerTech left-secondary / right-action footer pattern. */}
                {((!locked && !links.forward) || hasWorklistForFill) && (
                  <div className="ResultEntryPanel-sample-footer">
                    {/* SPEC-031: discard a draft sample physically lost/destroyed → an e-signed deviation
                        that spawns a re-run. Shown only while the result is a draft (not locked) and not
                        already discarded/re-run; the card keeps its record + shows the "Rerun created" badge. */}
                    {!locked && !links.forward && (
                      <div className="ResultEntryPanel-discard">
                        <Button variant="outline-danger" size="sm" onClick={() => setDiscardingSample(s)} disabled={saving}>
                          {t("limsDiscardAction")}
                        </Button>
                      </div>
                    )}

                    {/* F9 (SPEC-041) + RR7 (#846): the sample-prep worklist link — shown when a worklist
                        can be FILLED for this analysis (its own effective template, ELSE the generic
                        fallback). Opens the WorklistModal for THIS sample (the worklist is keyed per
                        sample-analysis and retrievable bagefter, even after the result is locked). A
                        discreet badge shows whether it is signed yet. NB: gated on hasWorklistForFill,
                        NOT hasWorklistTemplate — the latter (specific-only) is the send-gate, not the link. */}
                    {hasWorklistForFill && (
                      <div className="ResultEntryPanel-worklist">
                        <Button variant="outline-secondary" size="sm" onClick={() => setWorklistSample(s)}>
                          {t("limsWorklistOpen")}
                        </Button>
                        {sampleWorklists.some(
                          (w) => w.sampleId === s.id && w.analysisId === analysisId && w.state === "signed"
                        ) ? (
                          <StatusBadge variant="pass">{t("limsWorklistSignedBadge")}</StatusBadge>
                        ) : (
                          <StatusBadge variant="warn">{t("limsWorklistNotSignedBadge")}</StatusBadge>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* SPEC-029 (RR4 #5): the reviewer per-sample controls (OK override / OOT / Open OOS)
                    moved OUT of the analyst card into the Reviewer phase table's per-row "Review"
                    editor below. The card now shows the entry grid + the status chain only. */}
              </section>
            )
          })}
        </div>
      )}

      {/* Soft warning: a selected reference standard is in quarantine or expiring soon. Allowed
          to proceed (never blocks); shown only while there's something sendable (SPEC-015). */}
      {anySendable && warningStandards.length > 0 && (
        <div className="ResultEntryPanel-standard-warning">
          <FiAlertTriangle />{" "}
          {t("limsStandardQuarantineWarning", { standards: warningStandards.map((c) => c.name).join(", ") })}
        </div>
      )}

      {/* HARD GATE (SPEC-015): an expired/discarded reference standard blocks send-to-review. The
          analyst selects a valid standard, or a qualified non-enterer signs a deviation override. */}
      {anySendable && blockingStandards.length > 0 && (
        <div className="ResultEntryPanel-standard-gate">
          <p className="ResultEntryPanel-standard-gate-title">
            <FiAlertTriangle /> {t("limsStandardGateBlockedTitle")}
          </p>
          <p className="ResultEntryPanel-standard-gate-body">
            {t("limsStandardGateBlockedBody", { standards: blockingStandards.map((c) => c.name).join(", ") })}
          </p>
          <Button variant="outline-danger" onClick={() => setShowOverrideGate(true)} disabled={saving}>
            {t("limsStandardGateOverride")}
          </Button>
        </div>
      )}

      {/* HARD GATE (SPEC-017): the analysis requires a System Suitability Test that has not yet
          passed + been signed. The analyst completes + signs the SST panel above, or a qualified
          non-enterer signs a deviation override. */}
      {anySendable && requiresSst && !sstReady && (
        <div className="ResultEntryPanel-standard-gate">
          <p className="ResultEntryPanel-standard-gate-title">
            <FiAlertTriangle /> {t("limsSstGateBlockedTitle")}
          </p>
          <p className="ResultEntryPanel-standard-gate-body">{t("limsSstGateBlockedBody")}</p>
          <Button variant="outline-danger" onClick={() => setShowOverrideGate(true)} disabled={saving}>
            {t("limsSstGateOverride")}
          </Button>
        </div>
      )}

      {/* #4142: rendered on `anyAnalystWork` (state), not `anySendable` (state AND capability), so a
          reviewer or QA looking at an analyst's drafts sees the analyst's actions GHOSTED rather
          than missing — the customer's "ghosted so it does not look like it works", and the same
          treatment the reviewer/supervisor/QA actions have always had. Both buttons stay disabled
          for them, and carry the capability sentence as their tooltip. */}
      {anyAnalystWork && (
        <div className="ResultEntryPanel-actions">
          {/* #4107: available whenever there is something entered — a draft is allowed to be
              incomplete, so this never carries the send gates' disabled state. */}
          <Button
            variant="outline-secondary"
            onClick={handleSaveDraft}
            disabled={saving || !canEnterResults}
            title={analystGhostHint || t("limsResultSaveDraftHint")}
          >
            {t("limsResultSaveDraft")}
          </Button>
          <Button
            variant="primary"
            onClick={handleSendAll}
            disabled={saving || !canEnterResults || !allSamplesReady}
            title={analystGhostHint || sendBlockedHint || undefined}
          >
            {t("limsResultSendToReview")}
          </Button>
          {sendBlockedHint && <p className="ResultEntryPanel-send-incomplete">{sendBlockedHint}</p>}
        </div>
      )}
      </PhaseSection>

      {/* SPEC-029 (RR4 #5): REVIEWER phase — a QA-like table, one row per in-review sample (the
          parameters folded into Score / Interval), with the reviewer actions (override / OOT / Open
          OOS) in a per-row "Review" editor. Send to QA / Return are the batch actions. Always shown;
          ghosted (with the four-eyes/capability hint) for a viewer who cannot review — reviewGate +
          the e-signature dialog enforce the gate authoritatively. */}
      <PhaseSection
        title={t("limsPhaseReviewer")}
        stepLabel="2"
        active={activePhase === "reviewer"}
        controlsGhosted={reviewerControlsGhosted}
        empty={!hasInReview}
        hint={hasInReview && !reviewGate.allowed ? reviewerGhostHint : null}
      >
        {hasInReview ? (
          <>
            <Table responsive className="ResultEntryPanel-review-table">
              <thead>
                <tr>
                  <th>{t("limsResultSample")}</th>
                  <th>{t("limsResultSampleId")}</th>
                  <th>{t("limsResultProduct")}</th>
                  <th>{t("limsReviewScore")}</th>
                  <th>{t("limsReviewInterval")}</th>
                  <th>{t("limsResultJudgement")}</th>
                  <th>{t("limsReviewActionCol")}</th>
                </tr>
              </thead>
              <tbody>
                {inReviewResults.map((r) => {
                  const s = sampleItems.find((x) => x.id === r.sampleId) || {}
                  const overridden = !!(r.okOverride && r.overall === "FAIL")
                  return (
                    <tr key={r.id}>
                      <td>{s.sampleName || "—"}</td>
                      <td>{displaySampleId(s.observationId) || "—"}</td>
                      <td>{s.productName || t("none")}</td>
                      <td>
                        <ul className="ResultEntryPanel-review-params">
                          {(r.values || []).map((v, i) => (
                            <li key={v.parameterId || i}>
                              <span className="ResultEntryPanel-review-pname">
                                {v.name}{v.unit ? ` (${v.unit})` : ""}:
                              </span>{" "}
                              {formatScore(v)}
                            </li>
                          ))}
                        </ul>
                      </td>
                      <td>
                        <ul className="ResultEntryPanel-review-params">
                          {(r.values || []).map((v, i) => (
                            <li key={v.parameterId || i}>{criterionText(v, t)}</li>
                          ))}
                        </ul>
                      </td>
                      <td>
                        <JudgementBadge raw={limsResults.effectiveOverall(r)} t={t} />
                        {overridden && (
                          <span className="ResultEntryPanel-qa-override"> ({t("limsResultOverrideShort")})</span>
                        )}
                        {r.oot && (
                          <div className="ResultEntryPanel-qa-oot" title={r.ootComment || ""}>
                            {t("limsResultOot")}{r.ootComment ? `: ${r.ootComment}` : ""}
                          </div>
                        )}
                      </td>
                      <td>
                        <Button
                          variant="link"
                          size="sm"
                          className="ResultEntryPanel-qa-amend"
                          onClick={() => setReviewEditId(r.id)}
                          disabled={saving || !reviewGate.allowed}
                          title={reviewerGhostHint || undefined}
                        >
                          {t("limsReviewEditAction")}
                        </Button>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </Table>
            <div className="ResultEntryPanel-review-actions">
              {/* SPEC-035 (F3): the reviewer's hand-off target depends on the analysis — to the
                  supervisor when it requires a supervisor review, else straight to QA (unchanged). */}
              <Button variant="success" onClick={() => setShowQaModal(true)} disabled={saving || !reviewGate.allowed}>
                {requiresSupervisorReview ? t("limsResultSendToSupervisor") : t("limsResultSendToQa")}
              </Button>
              <Button variant="warning" onClick={() => setShowReturnModal(true)} disabled={saving || !reviewGate.allowed}>
                {t("limsResultReviewReturn")}
              </Button>
            </div>
            {!reviewGate.allowed && (
              <p className="ResultEntryPanel-review-awaiting">{reviewerGhostHint || t("limsResultAwaitingReview")}</p>
            )}
          </>
        ) : (
          <p className="ResultEntryPanel-message">{t("limsReviewEmpty")}</p>
        )}
      </PhaseSection>

      {/* SPEC-035 (F3): SUPERVISOR phase — only when the analysis requires a supervisor review.
          Same QA-like table + per-row "Review" editor as the reviewer phase (the supervisor has FULL
          review power: override / OOT / Open OOS), with "Send to QA" / "Return to reviewer" batch
          actions. Always shown (ghosted for a viewer who cannot supervise); supervisorGate + the
          e-signature dialog enforce the gate authoritatively. */}
      {requiresSupervisorReview && (
        <PhaseSection
          title={t("limsPhaseSupervisor")}
          stepLabel="3"
          active={activePhase === "supervisor"}
          controlsGhosted={supervisorControlsGhosted}
          empty={!hasSentToSupervisor}
          hint={hasSentToSupervisor && !supervisorGate.allowed ? supervisorGhostHint : null}
        >
          {hasSentToSupervisor ? (
            <>
              <Table responsive className="ResultEntryPanel-review-table">
                <thead>
                  <tr>
                    <th>{t("limsResultSample")}</th>
                    <th>{t("limsResultSampleId")}</th>
                    <th>{t("limsResultProduct")}</th>
                    <th>{t("limsReviewScore")}</th>
                    <th>{t("limsReviewInterval")}</th>
                    <th>{t("limsResultJudgement")}</th>
                    <th>{t("limsReviewActionCol")}</th>
                  </tr>
                </thead>
                <tbody>
                  {sentToSupervisorResults.map((r) => {
                    const s = sampleItems.find((x) => x.id === r.sampleId) || {}
                    const overridden = !!(r.okOverride && r.overall === "FAIL")
                    return (
                      <tr key={r.id}>
                        <td>{s.sampleName || "—"}</td>
                        <td>{displaySampleId(s.observationId) || "—"}</td>
                        <td>{s.productName || t("none")}</td>
                        <td>
                          <ul className="ResultEntryPanel-review-params">
                            {(r.values || []).map((v, i) => (
                              <li key={v.parameterId || i}>
                                <span className="ResultEntryPanel-review-pname">
                                  {v.name}{v.unit ? ` (${v.unit})` : ""}:
                                </span>{" "}
                                {formatScore(v)}
                              </li>
                            ))}
                          </ul>
                        </td>
                        <td>
                          <ul className="ResultEntryPanel-review-params">
                            {(r.values || []).map((v, i) => (
                              <li key={v.parameterId || i}>{criterionText(v, t)}</li>
                            ))}
                          </ul>
                        </td>
                        <td>
                          <JudgementBadge raw={limsResults.effectiveOverall(r)} t={t} />
                          {overridden && (
                            <span className="ResultEntryPanel-qa-override"> ({t("limsResultOverrideShort")})</span>
                          )}
                          {r.oot && (
                            <div className="ResultEntryPanel-qa-oot" title={r.ootComment || ""}>
                              {t("limsResultOot")}{r.ootComment ? `: ${r.ootComment}` : ""}
                            </div>
                          )}
                        </td>
                        <td>
                          <Button
                            variant="link"
                            size="sm"
                            className="ResultEntryPanel-qa-amend"
                            onClick={() => setReviewEditId(r.id)}
                            disabled={saving || !supervisorGate.allowed}
                            title={supervisorGhostHint || undefined}
                          >
                            {t("limsReviewEditAction")}
                          </Button>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </Table>
              <div className="ResultEntryPanel-review-actions">
                <Button variant="success" onClick={() => setShowSupervisorModal(true)} disabled={saving || !supervisorGate.allowed}>
                  {t("limsResultSupervisorApprove")}
                </Button>
                <Button variant="warning" onClick={() => setShowSupervisorReturnModal(true)} disabled={saving || !supervisorGate.allowed}>
                  {t("limsSupervisorReturn")}
                </Button>
              </div>
              {!supervisorGate.allowed && (
                <p className="ResultEntryPanel-review-awaiting">{supervisorGhostHint || t("limsResultAwaitingReview")}</p>
              )}
            </>
          ) : (
            <p className="ResultEntryPanel-message">{t("limsSupervisorEmpty")}</p>
          )}
        </PhaseSection>
      )}

      {sentToQaCount > 0 && inReviewResults.length === 0 && sentToSupervisorResults.length === 0 && (
        <p className="ResultEntryPanel-reviewed-note">{t("limsResultSentToQaNote")}</p>
      )}

      {/* SPEC-022 (K5) / SPEC-029 (RR4 #5): the QA phase — per-sample QA decisions
          (QaDecisionSection), framed as the final phase. Renders nothing until the analysis has
          QA-stage results; the frame still shows so the lifecycle is visible. SPEC-035 (F3): the
          step number is 4 when the supervisor phase precedes it, else 3. */}
      <PhaseSection
        title={t("limsPhaseQa")}
        stepLabel={requiresSupervisorReview ? "4" : "3"}
        active={activePhase === "qa"}
        controlsGhosted={qaControlsGhosted}
        empty={!hasQaStage}
      >
        <QaDecisionSection analysisId={analysisId} analysisLabel={analysis ? analysis.name : ""} />
      </PhaseSection>

      {/* SPEC-034 (F2): the analysis-scoped audit trail, AFTER the three lifecycle phases. A
          collapsible, read-only section that lazy-fetches this analysis's own audit entries on its
          first expand (kept separate from the global AuditViewer trail). */}
      <AuditSection analysisId={analysisId} />

      {showQaModal && (
        <ESignatureModal
          title={requiresSupervisorReview ? t("limsResultSendToSupervisor") : t("limsResultSendToQa")}
          requiredCapability="reviewer"
          excludedMemberIds={enterers}
          commentContext="reviewer"
          onSign={handleSendToQaSign}
          onCancel={() => setShowQaModal(false)}
        />
      )}

      {/* SPEC-035 (F3): the SUPERVISOR approve-to-QA e-signature (supervisor capability, 4-eyes ≠
          analyst enterer(s) + technical reviewer(s)). */}
      {showSupervisorModal && (
        <ESignatureModal
          title={t("limsResultSupervisorApprove")}
          requiredCapability="supervisor"
          excludedMemberIds={supervisorExcludedMemberIds}
          commentContext="reviewer"
          onSign={handleSupervisorApproveSign}
          onCancel={() => setShowSupervisorModal(false)}
        />
      )}

      {/* RR2 (Q2): OOS investigation modal, opened from a FAIL result above. */}
      {activeOosId && (
        <OosInvestigationModal
          oosId={activeOosId}
          onClose={() => setActiveOosId(null)}
          onClosed={() => setActiveOosId(null)}
        />
      )}

      {/* F9 (SPEC-041): the per-sample sample-prep worklist popup. */}
      {worklistSample && (
        <WorklistModal
          sampleId={worklistSample.id}
          analysisId={analysisId}
          analysisDefinitionId={analysisDefinitionId || null}
          sampleLabel={`${worklistSample.sampleName || ""}${worklistSample.observationId ? ` · ${displaySampleId(worklistSample.observationId)}` : ""}`}
          primaryAssetId={primaryAssetId}
          reagentLotIds={reagentLotIds}
          onClose={() => setWorklistSample(null)}
        />
      )}

      {/* The okOverride e-signature. SPEC-035 (F3): in the supervisor phase the override is a
          supervisor disposition (supervisor capability, 4-eyes ≠ analyst + reviewer); in the
          reviewer phase it stays the reviewer's (reviewer capability, 4-eyes ≠ enterer). */}
      {overriding && (
        <ESignatureModal
          title={t("limsResultOverride")}
          requiredCapability={overriding.state === "sent-to-supervisor" ? "supervisor" : "reviewer"}
          excludedMemberIds={overriding.state === "sent-to-supervisor" ? supervisorExcludedMemberIds : enterers}
          onSign={handleOverrideSign}
          onCancel={() => setOverriding(null)}
        />
      )}

      {/* SPEC-031: the "Discard (lost/destroyed)" e-signature — a single-signer deviation with a
          MANDATORY free-text reason (meaning "authorized"). No capability/four-eyes: it is the
          analyst's contemporaneous record of a physical event, not a data judgement. */}
      {discardingSample && (
        <ESignatureModal
          title={t("limsDiscardTitle")}
          requireReason
          onSign={handleDiscardSign}
          onCancel={() => setDiscardingSample(null)}
        />
      )}

      {/* Send-to-review gate override (SPEC-021): ONE reviewer deviation signature authorises
          whichever hard gate(s) block the send — its password rides on the re-issued PATCH and
          the SERVER signs the deviation + audits each overridden gate. 4-eyes: not the enterers,
          not the sender, and — when the SST gate is among the blockers — not the SST's own
          enterer/signer (SPEC-020: no self-vouching for one's own SST; mirrored server-side). */}
      {showOverrideGate && (
        <ESignatureModal
          title={t(blockingStandards.length > 0 ? "limsStandardGateBlockedTitle" : "limsSstGateBlockedTitle")}
          requiredCapability="reviewer"
          excludedMemberIds={Array.from(
            new Set(
              [
                ...enterers,
                currentMemberId,
                ...(requiresSst && !sstReady
                  ? [sstRecord && sstRecord.enteredBy, sstRecord && sstRecord.signature && sstRecord.signature.memberId]
                  : []),
              ].filter(Boolean)
            )
          )}
          onSign={handleGateOverrideSign}
          onCancel={() => setShowOverrideGate(false)}
        />
      )}

      {showReturnModal && (
        <Popup title={t("limsResultReviewReturn")} onCancel={() => setShowReturnModal(false)}>
          <Container>
            <Title>{t("limsResultReviewReturnComment")}</Title>
            <textarea
              className="ResultEntryPanel-return-comment"
              rows={3}
              value={returnComment}
              onChange={(e) => setReturnComment(e.target.value)}
            />
          </Container>
          <div className="ResultEntryPanel-review-actions">
            <Button variant="light" onClick={() => setShowReturnModal(false)}>
              {t("cancel")}
            </Button>
            <Button variant="warning" onClick={handleReturnSubmit} disabled={!returnComment.trim()}>
              {t("limsResultReviewReturn")}
            </Button>
          </div>
        </Popup>
      )}

      {/* SPEC-035 (F3): the SUPERVISOR return-to-reviewer modal — mandatory comment, not e-signed. */}
      {showSupervisorReturnModal && (
        <Popup title={t("limsSupervisorReturn")} onCancel={() => setShowSupervisorReturnModal(false)}>
          <Container>
            <Title>{t("limsSupervisorReturnComment")}</Title>
            <textarea
              className="ResultEntryPanel-return-comment"
              rows={3}
              value={supervisorReturnComment}
              onChange={(e) => setSupervisorReturnComment(e.target.value)}
            />
          </Container>
          <div className="ResultEntryPanel-review-actions">
            <Button variant="light" onClick={() => setShowSupervisorReturnModal(false)}>
              {t("cancel")}
            </Button>
            <Button variant="warning" onClick={handleSupervisorReturnSubmit} disabled={!supervisorReturnComment.trim()}>
              {t("limsSupervisorReturn")}
            </Button>
          </div>
        </Popup>
      )}

      {/* SPEC-029 (RR4 #5): the reviewer per-row "Review" editor — the override / OOT (+comment) /
          Open OOS controls that used to sit inline on each card. Same handlers + gates as before. */}
      {reviewEditRecord && (
        <Popup className="ResultEntryPanel-review-edit-popup" title={t("limsReviewEditTitle")} onCancel={() => setReviewEditId(null)}>
          {/* RR5 #1/#2: a tidy vertical body (no InputGroup flex-row, which crammed the controls
              onto one line and misaligned the checkbox) + a real footer action row — the Open OOS
              action moved down next to a primary "Done", PlannerTech modal style. */}
          <div className="ResultEntryPanel-review-edit-body">
            <p className="ResultEntryPanel-review-edit-sample">
              <strong>{reviewEditSample.sampleName || "—"}</strong>
              {reviewEditSample.observationId ? ` · ${displaySampleId(reviewEditSample.observationId)}` : ""}
              {` · ${reviewEditSample.productName || t("none")}`}
            </p>
            {reviewEditRecord.overall === "FAIL" && (
              <label className="ResultEntryPanel-reviewer-line">
                <Checkbox
                  checked={!!reviewEditRecord.okOverride}
                  onChange={(v) => handleSetOverride(reviewEditRecord, v)}
                  disabled={saving || !reviewEditGate.allowed}
                />
                <span title={t("limsResultOverrideHelp")}>{t("limsResultOverride")}</span>
              </label>
            )}
            <label className="ResultEntryPanel-reviewer-line">
              <Checkbox
                checked={!!reviewEditRecord.oot}
                onChange={(v) => handleSetOot(reviewEditRecord, v)}
                disabled={saving || !reviewEditGate.allowed}
              />
              <span>{t("limsResultOot")}</span>
            </label>
            {reviewEditRecord.oot && (
              <input
                type="text"
                className="ResultEntryPanel-oot-comment"
                placeholder={t("limsResultOotComment")}
                value={ootDrafts[reviewEditRecord.id] !== undefined ? ootDrafts[reviewEditRecord.id] : reviewEditRecord.ootComment || ""}
                onChange={(e) => setOotDrafts((prev) => ({ ...prev, [reviewEditRecord.id]: e.target.value }))}
                onBlur={() => commitOotComment(reviewEditRecord)}
                disabled={saving || !reviewEditGate.allowed}
              />
            )}
            {!reviewEditGate.allowed && <p className="ResultEntryPanel-reviewer-ghost">{reviewEditGhostHint}</p>}
          </div>
          <div className="ResultEntryPanel-review-actions">
            {reviewEditRecord.overall === "FAIL" && !limsResults.isQaDecided(reviewEditRecord) && (
              <Button variant="danger" onClick={() => handleOpenInvestigation(reviewEditSample, "OOS")} disabled={saving}>
                {t("limsOosOpenInvestigation")}
              </Button>
            )}
            {/* SPEC-037 (F5): an OOT-marked result opens an explicit OOT Phase-1 investigation (an
                OObS — never auto). Shown when the reviewer has marked the result out-of-trend and
                it is not already QA-decided. A FAIL row may carry both an OOS and an OOT button. */}
            {reviewEditRecord.oot && !limsResults.isQaDecided(reviewEditRecord) && (
              <Button variant="warning" onClick={() => handleOpenInvestigation(reviewEditSample, "OOT")} disabled={saving}>
                {t("limsOpenOotInvestigation")}
              </Button>
            )}
            <Button variant="primary" className="ResultEntryPanel-review-done" onClick={() => setReviewEditId(null)}>
              {t("limsReviewEditDone")}
            </Button>
          </div>
        </Popup>
      )}
    </div>
  )
}

// The aggregate input key for a calculated parameter (the input the analyst enters as an n-value
// list), or null when the parameter is not an aggregate one.
function aggregateKeyOf(param) {
  return param && param.aggregate && param.aggregate.inputKey ? param.aggregate.inputKey : null
}


// Read-only parameter rows for a LOCKED record (SPEC-020): rendered from the record's OWN
// specSnapshot + persisted values, NEVER from the live spec — the live spec may have been edited
// since the result was sent, and the certified figures must show against the spec they were
// actually judged by (the same source the QA decisions section renders from). A legacy record
// without a snapshot falls back to synthesising the row list from its persisted values (each
// value carries the judged name/unit/criterion).
function LockedRecordRows({ record, t }) {
  const snapshotParams =
    record.specSnapshot && Array.isArray(record.specSnapshot.parameters) && record.specSnapshot.parameters.length > 0
      ? record.specSnapshot.parameters
      : (record.values || []).map((v) => ({
          id: v.parameterId,
          name: v.name,
          unit: v.unit,
          criterionType: v.criterionType,
          min: v.min,
          max: v.max,
        }))
  if (snapshotParams.length === 0) return null
  const valueByParam = {}
  ;(record.values || []).forEach((v) => {
    valueByParam[v.parameterId] = v
  })
  return (
    <table className="ResultEntryPanel-sample-grid">
      <tbody>
        {snapshotParams.map((p) => {
          const v = valueByParam[p.id]
          return (
            <tr key={p.id}>
              <th className="ResultEntryPanel-grid-label">
                {p.name}
                {p.unit ? ` (${p.unit})` : ""}
                <div className="ResultEntryPanel-criterion">{criterionText(p, t)}</div>
              </th>
              <td>
                <span className="ResultEntryPanel-locked-value">
                  {v && v.value !== null && v.value !== undefined ? String(v.value) : "—"}
                </span>
              </td>
            </tr>
          )
        })}
      </tbody>
    </table>
  )
}

// Build one sample's editable entry from any saved result: measured values restored as strings,
// and each calculated parameter's inputs restored (or auto-filled) per declared input key.
function buildSeededEntry(record, parameters, autoFillForSource) {
  const values = {}
  const inputs = {}
  const recordedByParam = {}
  ;(record ? record.values || [] : []).forEach((v) => {
    recordedByParam[v.parameterId] = v
    values[v.parameterId] = v.value === null || v.value === undefined ? "" : String(v.value)
  })
  parameters.forEach((p) => {
    if (!limsSpecs.isCalculatedParameter(p)) return
    inputs[p.id] = seedParamInputs(p, recordedByParam[p.id], autoFillForSource)
  })
  // R19 (#4084): a saved record IS the analyst's attestation — the same rule the server judges by
  // (SPEC-056 R20) — so an OK'd parameter-less sample restores as OK'd after a reload or a
  // return-for-correction. Only read on the no-parameters branch.
  return { values, inputs, ok: !!record }
}

// Restore one calculated parameter's editable inputs: the snapshotted raw inputs when present,
// else a reference-standard auto-fill, else blank — keyed by the declared input keys.
function seedParamInputs(param, recorded, autoFillForSource) {
  const recordedInputs = (recorded && recorded.inputs) || {}
  const paramInputs = {}
  ;(param.inputs || []).forEach((inp) => {
    paramInputs[inp.key] = seedInputValue(inp, recordedInputs[inp.key], autoFillForSource)
  })
  return paramInputs
}

// Seed one calculated input's editable value: prefer the snapshotted recorded value (a string, or
// an array of strings for an aggregate input); else, for a reference-standard-sourced input, the
// defensively-resolved auto-fill value; else blank ("" scalar). The shape (scalar string vs array)
// follows the recorded value, so an aggregate input restores its full n-value list.
function seedInputValue(input, recorded, autoFillForSource) {
  if (Array.isArray(recorded)) return recorded.map((x) => (x === null || x === undefined ? "" : String(x)))
  if (recorded !== null && recorded !== undefined && recorded !== "") return String(recorded)
  const auto = autoFillForSource(input && input.source)
  return auto === null || auto === undefined ? "" : String(auto)
}

// The calculated-parameter input ROWS for one sample's 2-column grid (SPEC-019 redesign): the
// parameter name as a sub-header row, then one label|value row per declared input (an aggregate
// input spans both columns as a small add/remove list editor; a parameter-sourced input is
// read-only with a "from parameter X" note, showing the value RESOLVED from the referenced
// sibling parameter — SPEC-023), then a row with the engine's live ROUNDED computed value (or an
// inline calc error → judgement falls back to "—"). MEASURED parameters never reach this
// component.
function CalculatedInputRows({ param, inputValues, judged, sourceNameOf, disabled, onScalarChange, onAggregateChange, t }) {
  const aggregateKey = aggregateKeyOf(param)
  const inputs = param.inputs || []
  const computed = judged && judged.rounded
  const error = judged && judged.error
  return (
    <>
      <tr className="ResultEntryPanel-grid-subhead">
        <th className="ResultEntryPanel-grid-label">
          {param.name}
          {param.unit ? ` (${param.unit})` : ""}
          <span className="ResultEntryPanel-type"> · {t("limsSpecKindCalculated")}</span>
          <div className="ResultEntryPanel-criterion">{criterionText(param, t)}</div>
        </th>
        <td>
          {error ? (
            <span className="ResultEntryPanel-calc-error" title={error}>
              {t("limsResultCalcError")}
            </span>
          ) : (
            <span className="ResultEntryPanel-calc-computed-value">
              {computed === null || computed === undefined ? "—" : String(computed)}
              {param.unit ? ` ${param.unit}` : ""}
            </span>
          )}
        </td>
      </tr>
      {inputs.length === 0 && (
        <tr>
          <td colSpan={2} className="ResultEntryPanel-calc-empty">
            {t("limsResultCalcNoInputs")}
          </td>
        </tr>
      )}
      {inputs.map((inp) => {
        const label = inp.label || inp.key
        const labelWithUnit = inp.unit ? `${label} (${inp.unit})` : label
        // SPEC-023: a parameter-bound input is never typed — the engine binds it to the source
        // parameter's ROUNDED value; this cell just shows what was resolved (blank while the
        // source has no value yet → this parameter stays pending).
        if (inp.source === "parameter") {
          const resolved = judged && judged.resolvedInputs ? judged.resolvedInputs[inp.key] : undefined
          return (
            <tr key={inp.key}>
              <th className="ResultEntryPanel-grid-label">
                {labelWithUnit}
                <span className="ResultEntryPanel-calc-auto">
                  {" "}
                  {t("limsResultCalcFromParameter", { name: sourceNameOf(inp.parameterId) })}
                </span>
              </th>
              <td>
                <Text
                  value={resolved === undefined || resolved === null ? "" : String(resolved)}
                  onChange={() => {}}
                  disabled
                />
              </td>
            </tr>
          )
        }
        const autoFilled = inp.source && inp.source !== "manual"
        if (inp.key === aggregateKey) {
          return (
            <tr key={inp.key}>
              <td colSpan={2}>
                <AggregateInput
                  label={labelWithUnit}
                  values={Array.isArray(inputValues[inp.key]) ? inputValues[inp.key] : []}
                  perValueChecks={judged && judged.perValueChecks}
                  disabled={disabled}
                  onChange={(list) => onAggregateChange(inp.key, list)}
                  t={t}
                />
              </td>
            </tr>
          )
        }
        return (
          <tr key={inp.key}>
            <th className="ResultEntryPanel-grid-label">
              {labelWithUnit}
              {autoFilled && <span className="ResultEntryPanel-calc-auto"> {t("limsResultCalcAutoFilled")}</span>}
            </th>
            <td>
              <Text
                value={inputValues[inp.key] === undefined || inputValues[inp.key] === null ? "" : String(inputValues[inp.key])}
                onChange={(v) => onScalarChange(inp.key, v)}
                disabled={disabled}
              />
            </td>
          </tr>
        )
      })}
    </>
  )
}

// Aggregate (n-value) list editor for a calculated parameter's aggregate input — the analyst adds
// one row per individual value (e.g. each tablet weight). Each row shows a ✓/✗ when an individual
// ±tolerance limit applies and the value is out of it.
function AggregateInput({ label, values, perValueChecks, disabled, onChange, t }) {
  const setAt = (idx, v) => onChange(values.map((x, i) => (i === idx ? v : x)))
  const addRow = () => onChange([...values, ""])
  const removeRow = (idx) => onChange(values.filter((_x, i) => i !== idx))
  return (
    <div className="ResultEntryPanel-calc-aggregate">
      <span className="ResultEntryPanel-calc-label">{label}</span>
      {values.map((v, idx) => {
        const check = Array.isArray(perValueChecks) ? perValueChecks[idx] : null
        return (
          <div className="ResultEntryPanel-calc-agg-row" key={idx}>
            <Text value={v === null || v === undefined ? "" : String(v)} onChange={(nv) => setAt(idx, nv)} disabled={disabled} />
            {check && (
              <span
                className={"ResultEntryPanel-calc-agg-check " + (check.withinTolerance ? "is-within" : "is-out")}
                title={check.withinTolerance ? t("limsResultCalcWithinTol") : t("limsResultCalcOutOfTol")}
              >
                {check.withinTolerance ? "✓" : "✗"}
              </span>
            )}
            {!disabled && (
              <button
                type="button"
                className="ResultEntryPanel-calc-agg-remove"
                title={t("limsResultCalcRemoveValue")}
                onClick={() => removeRow(idx)}
              >
                <FiTrash2 />
              </button>
            )}
          </div>
        )
      })}
      {!disabled && (
        <button type="button" className="ResultEntryPanel-calc-agg-add" onClick={addRow}>
          <FiPlus /> {t("limsResultCalcAddValue")}
        </button>
      )}
    </div>
  )
}

// Human-readable acceptance criterion for a spec parameter (read-only reference).
// F8 (SPEC-040) — whether a selected consumable COVERS one requirement (mirrors the BE
// `_consumable_matches_requirement` + the AnalysisTaskConfig `matchesRequirement`): active, type
// matches, and (for a pinned standardClass) the standard class matches. Quantity is advisory.
function consumableCoversRequirement(consumable, requirement) {
  if (!consumable) return false
  if (validityStatus(consumable) !== "active") return false
  if (requirement.type && consumableType(consumable) !== requirement.type) return false
  if (requirement.standardClass && (consumable.standardClass || null) !== requirement.standardClass) return false
  return true
}

function criterionText(param, t) {
  const { criterionType, min, max } = param
  switch (criterionType) {
    case "min":
      return `≥ ${formatBound(min)}`
    case "max":
      return `≤ ${formatBound(max)}`
    case "range":
      return `${formatBound(min)} – ${formatBound(max)}`
    default:
      return t("limsResultNoCriterion")
  }
}

function formatBound(v) {
  return v === null || v === undefined || v === "" ? "—" : String(v)
}

// SPEC-029 (RR4 #5): the displayed score for one persisted result value (reviewer table). A measured
// value carries `value`; a calculated one carries the rounded computed value (value / computedValue /
// rounded depending on the engine output). Falls back to "—" when none is present.
function formatScore(v) {
  if (!v) return "—"
  const val = v.value ?? v.computedValue ?? v.rounded
  return val === null || val === undefined || val === "" ? "—" : String(val)
}

// SampleStatusChain (SPEC-022 F2 / K3, K16) — the per-sample status as a vertical chain of
// UNIFORM badges (the GMP decision narrative analyst → reviewer → QA), replacing the old mix of a
// Pass/Fail flag + parenthetical "(override)/(in review)/(Approved)" text:
//   row 1  RAW judgement: Pass (green) / Fail (red) / pending (grey) — always shown once judged.
//   row 2  REVIEWER action (when present): Override→effective Pass (amber), OOT (amber), and the
//          review-stage lifecycle state (In review / Sent to QA, amber).
//   row 3  QA decision (when present): Approved (green) / Rerun (blue) / Stopped (red).
// `raw` is the raw computed overall ("PASS" | "FAIL" | null); `record` is the persisted result
// (or undefined for an unsaved draft).
function SampleStatusChain({ raw, record, t }) {
  const state = record ? record.state : null
  const overridden = !!(record && record.okOverride && record.overall === "FAIL")
  const qaBadge = qaDecisionBadge(state, t)
  return (
    <StatusBadgeStack className="ResultEntryPanel-status-chain">
      <JudgementBadge raw={raw} t={t} />
      {overridden && (
        <StatusBadge variant="warn">
          {t("limsResultPass")} ({t("limsResultOverrideShort")})
        </StatusBadge>
      )}
      {record && record.oot && (
        <StatusBadge variant="warn" title={record.ootComment || ""}>
          {t("limsResultOot")}
        </StatusBadge>
      )}
      {state === "in-review" && <StatusBadge variant="warn">{t("limsResultInReviewShort")}</StatusBadge>}
      {state === "sent-to-qa" && <StatusBadge variant="warn">{t("limsResultSentToQaShort")}</StatusBadge>}
      {qaBadge}
    </StatusBadgeStack>
  )
}

// Raw Pass / Fail / "—" (not yet judged) uniform badge. `raw` is "PASS" | "FAIL" | null.
function JudgementBadge({ raw, t }) {
  if (raw === null || raw === undefined) {
    return <StatusBadge variant="pending">—</StatusBadge>
  }
  const pass = raw === "PASS"
  return <StatusBadge variant={pass ? "pass" : "fail"}>{pass ? t("limsResultPass") : t("limsResultFail")}</StatusBadge>
}

// The QA-decision badge for a terminal result state (or null for a non-QA state). Approved green,
// Rerun blue, Stopped red — the SAME variants the QA decisions table renders (DRY via StatusBadge).
function qaDecisionBadge(state, t) {
  switch (state) {
    case "qa-approved":
      return <StatusBadge variant="pass">{t("limsQaReviewedApproved")}</StatusBadge>
    case "qa-rerun":
      return <StatusBadge variant="info">{t("limsQaReviewedRerun")}</StatusBadge>
    case "qa-stopped":
      return <StatusBadge variant="fail">{t("limsQaReviewedStopped")}</StatusBadge>
    default:
      return null
  }
}
